/**
 * ============================================================================
 * CHAT AGENT API — RTK Query endpoint for GenerateEmbedUrlForChatAgent.
 * ============================================================================
 *
 * Fetches the signed QuickSight embed URL for a chat agent. The embed URL is a
 * user-specific, time-limited bearer credential (roughly five minutes to open,
 * a ten-hour session once opened), so — unlike the session-constant caches
 * (metricDefinitions, permissions) — it is NOT retained forever: a stale URL is
 * useless. It is fetched lazily on first open of the widget and refetchable via
 * a manual retry; the widget keeps the iframe mounted after the first load so
 * the opened session persists across collapse/expand.
 *
 * Transport lives in the accessor (ChatAgentApi); this endpoint is cache policy
 * only (queryFn → accessor). The alias is read at call time from currentUser
 * (the server re-resolves it from Midway anyway).
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { ChatAgentApi } from 'src/accessors/ChatAgentApi';
import { ReportingApiError } from 'src/accessors/ReportingApiClient';
import { currentUserAlias } from 'src/accessors/currentUser';
import {
  AgentName,
  GenerateEmbedUrlForChatAgentOutput,
} from 'src/types/chatAgentApi.types';

/**
 * Retention for the embed URL, in seconds. Short — the URL expires — but long
 * enough to cover the gap between fetch and the iframe loading it. Once the
 * widget mounts the iframe it keeps subscribing, so retention only governs the
 * brief unsubscribed window.
 */
const EMBED_URL_KEEP_SECONDS = 300;

export const chatAgentApi = createApi({
  reducerPath: 'chatAgentApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(),
  keepUnusedDataFor: EMBED_URL_KEEP_SECONDS,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    // Cache key is the agent name: one embed per agent. The alias is read at
    // call time (the server overrides Midway callers to their own alias).
    getChatAgentEmbedUrl: builder.query<
      GenerateEmbedUrlForChatAgentOutput,
      AgentName
    >({
      queryFn: async (agentName) => {
        try {
          return {
            data: await ChatAgentApi.generateEmbedUrl({
              agentName,
              userAlias: currentUserAlias(),
            }),
          };
        } catch (error) {
          if (error instanceof ReportingApiError) {
            return {
              error: {
                status: error.status,
                data: error.body ?? error.errorCode,
              },
            };
          }
          return { error: { status: 0, data: String(error) } };
        }
      },
    }),
  }),
});

export const useGetChatAgentEmbedUrlQuery =
  chatAgentApi.useGetChatAgentEmbedUrlQuery;
